/**
 * A deploy copies files into the live folder one by one: for a few minutes index.html may reference chunks that are
 * not uploaded yet, and the server answers them with index.html. Browsers (and Cloudflare) keep that HTML under the
 * chunk URL for hours, so a plain reload keeps failing. Recovery re-requests the build's assets bypassing the browser
 * cache, then reloads once. The same key is used by the inline script in index.html.
 */
export const STALE_ASSET_GUARD_KEY = "bordik_stale_asset_reload"
/** One automatic attempt per minute, so a file that is still missing cannot cause a reload loop. */
export const STALE_ASSET_GUARD_MS = 60_000

const MODULE_ERROR = /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i

const errorMessage = (error: unknown) => (
    error instanceof Error ? error.message : typeof error === "string" ? error : ""
)

/** Same-origin `/static/` asset named in a failed import, e.g. Chrome's "Failed to fetch dynamically imported module: URL". */
export const getFailedAssetUrl = (error: unknown): string | null => {
    const match = /(https?:\/\/[^\s'"]+?\/static\/[^\s'"]+?\.(?:js|css))/.exec(errorMessage(error))
    if (!match) return null
    try {
        const url = new URL(match[1])
        return url.origin === window.location.origin ? url.href : null
    } catch {
        return null
    }
}

export const isStaleAssetError = (error: unknown) => MODULE_ERROR.test(errorMessage(error)) || getFailedAssetUrl(error) !== null

/** Every build asset the page knows about: entry scripts, module preloads (also those Vite adds for lazy chunks) and CSS. */
export const getKnownAssetUrls = (failedUrl?: string | null): string[] => {
    const urls = new Set<string>()
    if (failedUrl) urls.add(failedUrl)
    document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>("script[src], link[rel=modulepreload][href], link[rel=stylesheet][href]")
        .forEach(element => {
            const value = element instanceof HTMLScriptElement ? element.src : element.href
            try {
                const url = new URL(value, window.location.href)
                if (url.origin === window.location.origin && url.pathname.startsWith("/static/")) urls.add(url.href)
            } catch {
                // Ignore malformed attributes.
            }
        })
    return [...urls]
}

const guardAllows = () => {
    try {
        const last = Number(window.sessionStorage.getItem(STALE_ASSET_GUARD_KEY) || 0)
        if (Date.now() - last < STALE_ASSET_GUARD_MS) return false
        window.sessionStorage.setItem(STALE_ASSET_GUARD_KEY, String(Date.now()))
        return true
    } catch {
        return false
    }
}

/**
 * Refreshes the cached copies of the build's assets and reloads. Automatic calls respect the per-minute guard; a click
 * on «Перезагрузить страницу» passes `force` and always retries.
 */
export const recoverFromStaleAssets = async (error: unknown, {force = false}: {force?: boolean} = {}): Promise<boolean> => {
    if (!force && !guardAllows()) return false
    await Promise.all(getKnownAssetUrls(getFailedAssetUrl(error)).map(url => (
        fetch(url, {cache: "reload", credentials: "same-origin"}).catch(() => undefined)
    )))
    window.location.reload()
    return true
}

/** Vite emits `vite:preloadError` when a lazy route chunk or one of its dependencies fails to load. */
export const installStaleAssetRecovery = () => {
    window.addEventListener("vite:preloadError", (event) => {
        void recoverFromStaleAssets((event as Event & {payload?: unknown}).payload)
    })
}
